import BreadcrumbsSection from "components/Breadcrumbs/BreadcrumbsSection";
import Button from "components/buttons/Button/Button";
import Decimal from "decimal.js-light";
import AbsencesTable from "modules/absence/components/AbsencesView/components/AbsencesTable/AbsencesTable";
import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ContentWrapper from "~/components/ContentWrapper";
import PageHeading from "~/components/headings/PageHeading.tsx";
import SectionHeading from "~/components/headings/SectionHeading.tsx";
import MainContent from "~/components/mainContent/MainContent";
import { useAuth } from "~/contexts/AuthContext";
import type { Absence } from "~/modules/absence/api/absence/absenceTypes.ts";
import { AbsenceTypeId } from "~/modules/absence/api/absence/absenceTypes.ts";
import type { AbsenceType } from "~/modules/absence/api/absenceType/absenceTypeTypes.ts";
import type { VacationLedgerReportType } from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryTypes.ts";
import AbsencePageTabs from "~/modules/absence/components/AbsencePageTabs";
import AbsencesTableRow
	from "~/modules/absence/components/AbsencesView/components/AbsencesTable/components/AbsencesTableRow";
import AbsenceViewStaffMemberSelect from "~/modules/absence/components/AbsenceViewStaffMemberSelect";
import RemainingVacationDays from "~/modules/absence/components/RemainingVacationDays";
import { useAbsenceActions } from "~/modules/absence/hooks/useAbsenceActions.tsx";
import { AbsenceTabName } from "~/modules/absence/types/absenceOverviewTypes.ts";
import { getAbsenceActionRules } from "~/modules/absence/utils/absenceRules.ts";
import { downloadAbsenceIcs } from "~/modules/absence/utils/downloadAbsenceIcs.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateRange, formatDateWithGermanMonth } from "~/utils/dateAndTimeUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type AbsencesViewProps = {
	absences: Absence[];
	absenceTypes: AbsenceType[];
	staffMemberId: string,
	vacationLedgerReport: VacationLedgerReportType;
};

const AbsencesView: FunctionComponent<AbsencesViewProps> = ({
	absences,
	absenceTypes,
	staffMemberId,
	vacationLedgerReport,
}) => {
	const { hasAnyPermission } = useAuth();
	const { t } = useTranslation();
	const userCanAdminVacations = hasAnyPermission(PermissionNames.CanManageAbsences);
	const remainingVacationDays = new Decimal(parseFloat(vacationLedgerReport.daysRemaining));

	const { data: allUsers } = useAllUsers();
	const { dialogs, openCreate, openDelete, openEdit } = useAbsenceActions({ absences, absenceTypes, staffMemberId });

	const onDownloadIcsClick = useCallback((absence: Absence) => {
		const absenceTypeDisplayName = absenceTypes.find((absenceType) => absenceType.id === absence.absenceTypeId)?.name || "";
		const user = allUsers?.find((user) => user.id === absence.staffMemberId);
		if (!user) return;
		downloadAbsenceIcs({
			absence,
			absenceTypeName: absenceTypeDisplayName,
			userEmail: user.email,
			userFullName: user.fullName,
		});
	}, [absenceTypes, allUsers]);

	const absenceTableRows = useMemo(() => {
		if (absences.length > 0) {
			return absences.sort(byObjectProperty("startDate", "desc")).map((absence) => {
				const isVacation = absence.absenceTypeId == AbsenceTypeId.Vacation;

				let absenceTypeDisplayName = "--";
				const absenceType = absenceTypes
					.find((absenceType) => absenceType.id === absence.absenceTypeId);
				if (absenceType) {
					absenceTypeDisplayName = t(normalizeTKey(`entities:absenceType.${absenceType.name}`));
				}

				const timeSpan = formatDateRange(new Date(absence.startDate), new Date(absence.endDate)) || "--";
				let vacationDays = null;

				if (isVacation) {
					let vacationDaysDecimal = new Decimal(absence.vacationLedgerEntry?.workDays || absence.workDays);
					if (vacationDaysDecimal.isNegative()) {
						vacationDaysDecimal = vacationDaysDecimal.negated();
					}
					vacationDays = vacationDaysDecimal.toString().replace(".", ",");
				}


				const { isDeletable, isEditable } = getAbsenceActionRules(absence, absenceType?.name, userCanAdminVacations);

				return <AbsencesTableRow key={"absence-" + absence.id}
										 absenceType={absenceTypeDisplayName}
										 comment={absence.comment}
										 createdAt={formatDateWithGermanMonth(new Date(absence.createdAt))}
										 timespan={timeSpan}
										 vacationDays={vacationDays}
										 isDeletable={isDeletable}
										 isEditable={isEditable}
										 onDownloadIcsClick={() => onDownloadIcsClick(absence)}
										 onEditClick={() => openEdit(absence.id)}
										 onDeleteClick={() => openDelete(absence.id)}
				/>;
			});
		}
		return [];
	}, [absences, absenceTypes, userCanAdminVacations, t, onDownloadIcsClick, openEdit, openDelete]);

	return (
		<MainContent>
			<BreadcrumbsSection pages={["Abwesenheiten"]}
								className="bg-white" />
			<PageHeading title={t("timetracking.title", "Abwesenheiten")} />
			<PageHeading.BottomBar>
				<div
					className="z-40 flex w-full items-center gap-2 gap-x-5 text-sm font-medium text-gray-700 hover:text-gray-900">
					<RemainingVacationDays remainingVacationDays={remainingVacationDays} />
					<AbsenceViewStaffMemberSelect userIsVacationManager={userCanAdminVacations}
												  staffMemberId={staffMemberId}
												  currentSubPath={AbsenceTabName.absences} />
				</div>
				<div className="w-5" />
				<Button onClick={() => openCreate()}>
					{t("projects.btnNewProject", "Neue Abwesenheit")}
				</Button>
			</PageHeading.BottomBar>
			<ContentWrapper className="isolate">
				<SectionHeading sticky
								style={{ top: 73 }}>
					<AbsencePageTabs selectedTabName={AbsenceTabName.absences} />
				</SectionHeading>
				<div>
					<AbsencesTable>
						{absenceTableRows}
					</AbsencesTable>
				</div>
			</ContentWrapper>
			{dialogs}
		</MainContent>
	);
};

export default AbsencesView;